import React, { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import { useStringsContext } from '../../../../../contexts/strings';
import BaseDialog, { BaseDialogProps } from '../../../../common/base-dialog';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../../firebase-app';
import { setDoc, collection, doc } from 'firebase/firestore';
import { getPollPath } from '../../../../../utils/paths';
import { FS_PATH_POLLS } from '../../../../../constants';
import { PlayerPoll, Poll, PlayerPollOption } from '../../types';
import { getStatus } from '../../data';
import PollResultsCard from '../cards/poll-results-card';
import PollNameTimeCard from '../cards/poll-name-time-card';
import PollPlayerDetailsCard from '../cards/poll-player-details-card';
import PollCoachDetailsCard from '../cards/poll-coach-details-card';
import PollImagesCard from '../cards/poll-images-card';
import PollTitle from '../poll-title';
import PollValidationPopover from '../poll-validation-popover';
import { useFormValidate } from '../../../../../hooks/formValidate';

interface Props extends BaseDialogProps {
  onEditDone: () => void;
  onCreateDone: (createdPoll: Poll) => void;
  onDelete: () => void;
  selectedPoll: PlayerPoll | null;
  selectedAbbrv: string;
  pollType: 'playerofthematch' | 'playerofthemonth' | 'coachofthemonth';
}

const PollPlayerModal = ({
  open,
  onClose,
  onEditDone,
  onCreateDone,
  onDelete,
  selectedPoll,
  selectedAbbrv,
  pollType,
}: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  const anchorEl = useRef<any>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideValidationError, setHideValidationError] = useState<boolean>(true);

  const isEditing = selectedPoll !== null;
  const pollStatus = isEditing ? getStatus(selectedPoll) : null;
  const isCoachPoll = pollType === 'coachofthemonth';

  const pollId = useMemo(() => {
    return selectedPoll ? selectedPoll.id : doc(collection(firebaseApp.db, FS_PATH_POLLS)).id;
  }, [selectedPoll]);

  const { addFormValue, addFormValidation, removeFormValue, isValid, isDirty, editedValues } =
    useFormValidate();

  const onSubmit = useCallback(async () => {
    if (!isDirty || !isValid) {
      setHideValidationError(false);
      setShowPopover(true);
      return;
    }

    setIsSubmitting(true);

    const updatedPoll = isEditing
      ? { ...selectedPoll }
      : ({
          id: pollId,
          abbrv: selectedAbbrv,
          type: pollType,
          createdAt: moment().toISOString(),
        } as PlayerPoll);
    Object.entries(editedValues).forEach(([key, value]) => {
      if (key === 'squadPlayers') {
        const pollOptions = (editedValues[key] as string[]).map((fogisId, index) => {
          return {
            id: index,
            fogisId: Number(fogisId),
            type: 'player',
          } satisfies PlayerPollOption;
        });
        updatedPoll['pollOptions'] = pollOptions;
      } else if (key === 'coachOptions') {
        updatedPoll['pollOptions'] = value as PlayerPollOption[];
      } else {
        (updatedPoll as any)[key] = value; // Annoying typescript error fix
      }
    });

    try {
      const pollPath = getPollPath(pollId);
      await setDoc(doc(firebaseApp.db, pollPath), updatedPoll);

      enqueueSnackbar(isEditing ? strings.polls.editSuccess : strings.messages.saveSuccess, {
        variant: 'success',
      });
      isEditing ? onEditDone() : onCreateDone(updatedPoll);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.error, {
        variant: 'error',
      });
    }
  }, [
    editedValues,
    enqueueSnackbar,
    isDirty,
    isEditing,
    isValid,
    onCreateDone,
    onEditDone,
    pollId,
    pollType,
    selectedAbbrv,
    selectedPoll,
    strings,
  ]);

  const handleValue = (key: string, value: any) => {
    addFormValue(key, value);
  };

  const handleValidation = (key: string, isValid: boolean) => {
    addFormValidation(key, isValid);
  };

  const onDeleteField = (key: string) => {
    removeFormValue(key);
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={
          pollStatus !== 'FINISHED'
            ? isEditing
              ? strings.polls.editPoll
              : isCoachPoll
                ? strings.polls.newCoachPoll
                : strings.polls.newPlayerPoll
            : undefined
        }
      >
        <DialogContent dividers ref={anchorEl}>
          <Stack spacing={4}>
            {isEditing && selectedPoll && (
              <PollTitle selectedPoll={selectedPoll} pollStatus={pollStatus} />
            )}
            {isEditing && selectedPoll && pollStatus !== 'UPCOMING' && (
              <PollResultsCard selectedPoll={selectedPoll} />
            )}
            {pollStatus !== 'FINISHED' && (
              <>
                <PollNameTimeCard
                  title={
                    isCoachPoll
                      ? strings.polls.pollNameCoachTitle
                      : strings.polls.pollNamePlayerTitle
                  }
                  label={
                    isCoachPoll
                      ? strings.polls.pollNameCoachLabel
                      : strings.polls.pollNamePlayerLabel
                  }
                  helperText={
                    isCoachPoll
                      ? strings.polls.pollNameCoachHelperText
                      : strings.polls.pollNamePlayerHelperText
                  }
                  titleStartEnd={strings.polls.pollPlayerTimeStartTitle}
                  selectedPoll={selectedPoll}
                  isEditing={isEditing}
                  onValue={handleValue}
                  onValidation={handleValidation}
                  hideValidationError={hideValidationError}
                />
                {isCoachPoll ? (
                  <PollCoachDetailsCard
                    selectedPoll={selectedPoll}
                    selectedAbbrv={selectedAbbrv}
                    onValue={handleValue}
                    onValidation={handleValidation}
                    hideValidationError={hideValidationError}
                  />
                ) : (
                  <PollPlayerDetailsCard
                    selectedPoll={selectedPoll}
                    selectedAbbrv={selectedAbbrv}
                    onValue={handleValue}
                    onValidation={handleValidation}
                    onDelete={onDeleteField}
                    hideValidationError={hideValidationError}
                  />
                )}
                <PollImagesCard
                  titleBackgroundImage={strings.polls.backgroundImageTitle}
                  pollId={pollId}
                  selectedPoll={selectedPoll}
                  onValue={handleValue}
                  onValidation={handleValidation}
                  hideValidationError={hideValidationError}
                />
                {isEditing && (
                  <Button
                    startIcon={<DeleteIcon />}
                    variant="outlined"
                    color="error"
                    onClick={onDelete}
                  >
                    {strings.polls.deletePoll}
                  </Button>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {pollStatus === 'FINISHED' ? (
            <Button variant="contained" color="primary" onClick={onClose}>
              {strings.actions.close}
            </Button>
          ) : (
            <>
              <Button color="primary" onClick={onClose}>
                {strings.actions.cancel}
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                disabled={!isDirty}
                loading={isSubmitting}
                onClick={onSubmit}
              >
                {strings.actions.save}
              </LoadingButton>
            </>
          )}
        </DialogActions>
      </BaseDialog>
      <PollValidationPopover
        showPopover={showPopover}
        anchorElRef={anchorEl.current}
        onClose={() => setShowPopover(false)}
      />
    </>
  );
};

export default PollPlayerModal;
